/**
 * gravur-anfrage.js  v9
 * =====================================================================
 * Submit-Architektur: System A (vollständig nativ / Shopify-Standard)
 * - Validierung via click-Handler (capture=true) auf Submit-Button
 * - Bei Fehlern: preventDefault + stopImmediatePropagation → kein Submit
 * - Bei valider Eingabe: nativer Shopify-Submit → Seite lädt neu
 * - Erfolg: Liquid posted_successfully? rendert Success-Komponente
 * - Kein fetch(), kein submit-Handler, keine Race-Conditions
 * =====================================================================
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────
     CONSTANTS
  ───────────────────────────────────────────────────────── */
  const BODY_MAX      = 1000;

  /* ─────────────────────────────────────────────────────────
     STATE
  ───────────────────────────────────────────────────────── */
  const state = {
    collections:        [],
    products:           [],
    fullProductData:    null,
    selectedCollection: null,
    selectedProduct:    null,
    selectedVariant:    null,
    selectedOptions:    {},
    submitAttempted:    false,
  };

  /* ─────────────────────────────────────────────────────────
     DOM
  ───────────────────────────────────────────────────────── */
  let d = {};

  function resolveDOM() {
    d = {
      form:                   document.getElementById('GravurAnfrageForm'),
      formBody:               document.getElementById('gravur-form-body'),
      successEl:              document.getElementById('gravur-success'),
      // Collection
      collectionSelect:       document.getElementById('gravur-collection-select'),
      collectionSpinner:      document.getElementById('gravur-collection-spinner'),
      collectionError:        document.getElementById('gravur-collection-error'),
      // Product
      productStep:            document.getElementById('gravur-step-product'),
      productSelect:          document.getElementById('gravur-product-select'),
      productSpinner:         document.getElementById('gravur-product-spinner'),
      productError:           document.getElementById('gravur-product-error'),
      // Product card
      productCard:            document.getElementById('gravur-product-card'),
      productCardImg:         document.getElementById('gravur-product-card-img'),
      productCardTitle:       document.getElementById('gravur-product-card-title'),
      productCardVariant:     document.getElementById('gravur-product-card-variant'),
      productClearBtn:        document.getElementById('gravur-product-clear'),
      // Variants
      variantsWrap:           document.getElementById('gravur-variants'),
      variantOptions:         document.getElementById('gravur-variant-options'),
      // Hidden
      hiddenCollectionTitle:  document.getElementById('gravur-hidden-collection-title'),
      hiddenProductTitle:     document.getElementById('gravur-hidden-product-title'),
      hiddenVariantTitle:     document.getElementById('gravur-hidden-variant-title'),
      hiddenIndiv:            document.getElementById('gravur-hidden-individualisierung'),
      // Checkboxes (kein name-Attribut)
      indivCheckboxes:        document.querySelectorAll('.gravur-checkbox'),
      // Quantity
      qtyInput:               document.getElementById('gravur-qty-input'),
      qtyError:               document.getElementById('gravur-qty-error'),
      qtyQuickBtns:           document.querySelectorAll('.gravur-qty__quick-btn'),
      // Contact
      vorname:                document.getElementById('gravur-vorname'),
      vornameError:           document.getElementById('gravur-vorname-error'),
      nachname:               document.getElementById('gravur-nachname'),
      nachnameError:          document.getElementById('gravur-nachname-error'),
      email:                  document.getElementById('gravur-email'),
      emailError:             document.getElementById('gravur-email-error'),
      // Body textarea
      bodyTextarea:           document.getElementById('gravur-body'),
      bodyCount:              document.getElementById('gravur-body-count'),
      // Submit button (type="button" – kein nativer Submit)
      submitBtn:              document.getElementById('gravur-submit-btn'),
      heroCTA:                document.querySelector('.gravur-hero__cta'),
    };
  }

  /* ─────────────────────────────────────────────────────────
     HERO CTA
  ───────────────────────────────────────────────────────── */
  function initHeroCTA() {
    if (!d.heroCTA) return;
    d.heroCTA.addEventListener('click', function (e) {
      const target = document.getElementById('gravur-formular');
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => { if (d.collectionSelect) d.collectionSelect.focus(); }, 520);
    });
  }

  /* ─────────────────────────────────────────────────────────
     COLLECTIONS
  ───────────────────────────────────────────────────────── */
  async function loadCollections() {
    if (!d.collectionSelect) return;
    showSpinner(d.collectionSpinner, true);
    try {
      const res  = await fetch('/collections.json?limit=250&sort_by=title-ascending',
        { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!res.ok) throw new Error(res.status);
      const data = await res.json();
      state.collections = (data.collections || []).filter(c => c.handle !== 'frontpage' && c.published_at);
      populateSelect(d.collectionSelect, state.collections, 'title', '— Kategorie auswählen —');
    } catch (err) {
      console.warn('[Gravur] Collections:', err);
      addErrorOption(d.collectionSelect, 'Kategorien konnten nicht geladen werden');
    } finally {
      showSpinner(d.collectionSpinner, false);
    }
  }

  function initCollectionSelect() {
    if (!d.collectionSelect) return;
    d.collectionSelect.addEventListener('change', async function () {
      const handle = this.value;
      resetProductState();
      if (!handle) {
        d.productStep.hidden = true;
        if (state.submitAttempted) setError(d.collectionSelect, d.collectionError, true);
        return;
      }
      setError(d.collectionSelect, d.collectionError, false);
      const col = state.collections.find(c => c.handle === handle);
      if (d.hiddenCollectionTitle) d.hiddenCollectionTitle.value = col?.title || handle;
      await loadProductsForCollection(handle);
    });
  }

  async function loadProductsForCollection(handle) {
    d.productStep.hidden = false;
    showSpinner(d.productSpinner, true);
    d.productSelect.innerHTML = '<option value="">— Produkt auswählen —</option>';
    d.productSelect.disabled  = true;
    try {
      const res  = await fetch(`/collections/${handle}/products.json?limit=250`,
        { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!res.ok) throw new Error(res.status);
      const data = await res.json();
      state.products = data.products || [];
      populateSelect(d.productSelect, state.products, 'title', '— Produkt auswählen —');
      d.productSelect.disabled = false;
    } catch (err) {
      console.warn('[Gravur] Products:', err);
      addErrorOption(d.productSelect, 'Produkte konnten nicht geladen werden');
    } finally {
      showSpinner(d.productSpinner, false);
    }
  }

  /* ─────────────────────────────────────────────────────────
     PRODUCTS
  ───────────────────────────────────────────────────────── */
  function initProductSelect() {
    if (!d.productSelect) return;
    d.productSelect.addEventListener('change', async function () {
      const handle = this.value;
      resetProductState(true);
      if (!handle) {
        d.productCard.hidden = true;
        if (state.submitAttempted) setError(d.productSelect, d.productError, true);
        return;
      }
      setError(d.productSelect, d.productError, false);
      const product = state.products.find(p => p.handle === handle);
      if (!product) return;

      state.selectedProduct        = product;
      d.hiddenProductTitle.value   = product.title;

      updateProductCardImage(product.images?.[0]?.src || '');
      d.productCardTitle.textContent = product.title;
      if (d.productCardVariant) d.productCardVariant.textContent = '';
      d.productCard.hidden = false;

      await loadVariants(handle);
    });
  }

  /* ─────────────────────────────────────────────────────────
     VARIANTS
     FIX Fehler 7: options können Strings ODER Objekte {name, position, values} sein.
     Shopify /products/handle.js liefert options je nach Konfiguration als:
       ["Farbe", "Grösse"]                          → String-Array (normal)
       [{name:"Farbe", position:1, values:[...]}, …] → Objekt-Array (einige Shops)
     Extraktion: typeof opt === 'object' ? opt.name : opt
  ───────────────────────────────────────────────────────── */
  async function loadVariants(handle) {
    try {
      const res  = await fetch(`/products/${handle}.js`,
        { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!res.ok) throw new Error(res.status);
      const data = await res.json();
      state.fullProductData = data;

      // options sicher als String-Array normalisieren
      const optionNames = (data.options || []).map(opt =>
        (typeof opt === 'object' && opt !== null) ? (opt.name || String(opt)) : String(opt)
      );

      const isSingleDefault = data.variants.length === 1
        && optionNames.length === 1
        && optionNames[0] === 'Title';

      if (isSingleDefault) {
        const v = data.variants[0];
        state.selectedVariant      = v;
        d.hiddenVariantTitle.value = '';
        d.variantsWrap.hidden = true;
        return;
      }

      buildVariantPicker(data, optionNames);
      d.variantsWrap.hidden = false;
    } catch (err) {
      console.warn('[Gravur] Variants:', err);
      d.variantsWrap.hidden = true;
    }
  }

  function buildVariantPicker(productData, optionNames) {
    d.variantOptions.innerHTML = '';
    optionNames.forEach((optionName, optionIdx) => {
      const values  = [...new Set(productData.variants.map(v => v.options[optionIdx]))];
      const isColor = /farbe|color|colour|couleur|colore/i.test(optionName);

      const group   = document.createElement('div');
      group.className = 'gravur-variant-group';

      const labelEl = document.createElement('span');
      labelEl.className   = 'gravur-variant-group__label';
      labelEl.textContent = optionName;  // jetzt garantiert ein String
      group.appendChild(labelEl);

      const btnWrap = document.createElement('div');
      btnWrap.className = isColor ? 'gravur-variant-buttons gravur-variant-buttons--swatches' : 'gravur-variant-buttons';
      btnWrap.setAttribute('role', 'radiogroup');
      btnWrap.setAttribute('aria-label', optionName);

      values.forEach(value => {
        const btn = document.createElement('button');
        btn.type  = 'button';
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', 'false');
        btn.setAttribute('aria-label', value);
        btn.dataset.optionName  = optionName;
        btn.dataset.optionValue = value;

        if (isColor) {
          btn.className = 'gravur-variant-btn gravur-swatch-btn';
          btn.style.setProperty('--swatch-color', resolveColor(value));
          btn.innerHTML = `<span class="gravur-swatch__inner" title="${esc(value)}"></span><span class="gravur-swatch__label">${esc(value)}</span>`;
        } else {
          btn.className   = 'gravur-variant-btn';
          btn.textContent = value;
        }

        btn.addEventListener('click', () => {
          btnWrap.querySelectorAll('.gravur-variant-btn').forEach(b => {
            b.classList.remove('is-selected'); b.setAttribute('aria-checked', 'false');
          });
          btn.classList.add('is-selected');
          btn.setAttribute('aria-checked', 'true');
          state.selectedOptions[optionName] = value;
          onVariantSelected(productData, optionNames);
        });
        btnWrap.appendChild(btn);
      });

      group.appendChild(btnWrap);
      d.variantOptions.appendChild(group);
    });
  }

  function onVariantSelected(productData, optionNames) {
    const matched = productData.variants.find(v =>
      optionNames.every((opt, i) => !state.selectedOptions[opt] || v.options[i] === state.selectedOptions[opt])
    );
    if (!matched) return;

    state.selectedVariant      = matched;
    d.hiddenVariantTitle.value = matched.title;

    if (d.productCardVariant) {
      d.productCardVariant.textContent = Object.values(state.selectedOptions).join(' / ') || '';
    }

    const img = matched.featured_image?.src || (typeof matched.featured_image === 'string' ? matched.featured_image : '');
    if (img) updateProductCardImage(img);
  }

  function updateProductCardImage(src) {
    if (!d.productCardImg || !src) return;
    d.productCardImg.src = src.replace(/(\.\w+)(\?.*)?$/, '_160x160_crop_center$1');
  }

  function resolveColor(v) {
    const map = {
      rot:'#e53e3e', dunkelrot:'#822727', weinrot:'#742a2a', orange:'#dd6b20',
      gelb:'#d69e2e', gold:'#b7791f', grün:'#38a169', dunkelgrün:'#276749',
      blau:'#3182ce', dunkelblau:'#2a4365', hellblau:'#63b3ed', marineblau:'#1a365d',
      lila:'#805ad5', violet:'#6b46c1', pink:'#d53f8c', rosa:'#fc8181',
      schwarz:'#1a202c', weiss:'#f7fafc', weiß:'#f7fafc', grau:'#718096',
      hellgrau:'#e2e8f0', dunkelgrau:'#4a5568', beige:'#f5f0e8', braun:'#92400e',
      silber:'#cbd5e0', bronze:'#c05621',
      red:'#e53e3e', blue:'#3182ce', green:'#38a169', yellow:'#d69e2e',
      black:'#1a202c', white:'#f7fafc', grey:'#718096', gray:'#718096',
      silver:'#cbd5e0', navy:'#1a365d', purple:'#805ad5', brown:'#92400e',
      rouge:'#e53e3e', bleu:'#3182ce', vert:'#38a169', noir:'#1a202c', blanc:'#f7fafc',
      // Erweitert: häufige Shopify-Farbnamen
      champagner:'#f7e7ce', anthrazit:'#4a4a4a', petrol:'#1a6b74', teal:'#2c7a7b',
      türkis:'#38b2ac', turkis:'#38b2ac', khaki:'#a0926a', oliv:'#6b7c3a', olive:'#6b7c3a',
      dunkeloliv:'#4a5320', mint:'#81e6d9', lachs:'#fc8181', koralle:'#f6ad55',
      senf:'#d69e2e', cognac:'#a0522d', sand:'#e8d5b7', natur:'#f5f0e8',
      kupfer:'#b87333', messing:'#b5a642', edelstahl:'#a8a9ad', titan:'#878681',
      transparent:'transparent', klar:'transparent',
    };
    // Fallback: Farbwort unbekannt → neutrales Grau statt ungültige CSS-Farbe
    const key = v.toLowerCase().trim();
    if (map[key]) return map[key];
    // Letzter Versuch: CSS-Farbname direkt verwenden (z.B. 'crimson', 'steelblue')
    // Ungültige CSS-Namen ergeben weisse Fläche → #aaa als sicherer Fallback
    return '#aaaaaa';
  }

  /* ─────────────────────────────────────────────────────────
     RESET
  ───────────────────────────────────────────────────────── */
  function resetProductState(keepCollection = false) {
    state.selectedProduct = null; state.selectedVariant = null;
    state.selectedOptions = {}; state.fullProductData = null;
    if (d.variantsWrap)    d.variantsWrap.hidden = true;
    if (d.variantOptions)  d.variantOptions.innerHTML = '';
    if (d.productCard)     d.productCard.hidden = true;
    if (d.hiddenProductTitle) d.hiddenProductTitle.value = '';
    if (d.hiddenVariantTitle) d.hiddenVariantTitle.value = '';
    if (!keepCollection && d.hiddenCollectionTitle) d.hiddenCollectionTitle.value = '';
  }

  function initProductClear() {
    if (!d.productClearBtn) return;
    d.productClearBtn.addEventListener('click', () => {
      if (d.collectionSelect) d.collectionSelect.value = '';
      if (d.productStep)      d.productStep.hidden     = true;
      setError(d.collectionSelect, d.collectionError, false);
      resetProductState();
    });
  }

  /* ─────────────────────────────────────────────────────────
     INDIVIDUALISIERUNG → Hidden Field
  ───────────────────────────────────────────────────────── */
  function initIndividualisierung() {
    if (!d.indivCheckboxes.length || !d.hiddenIndiv) return;
    d.indivCheckboxes.forEach(cb => cb.addEventListener('change', syncIndiv));
  }

  function syncIndiv() {
    const vals = [];
    d.indivCheckboxes.forEach(cb => { if (cb.checked) vals.push(cb.value); });
    d.hiddenIndiv.value = vals.join(', ');
  }

  /* ─────────────────────────────────────────────────────────
     QUANTITY
  ───────────────────────────────────────────────────────── */
  function initQuantity() {
    if (!d.qtyInput) return;

    d.qtyQuickBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        d.qtyInput.value = btn.dataset.qty;
        d.qtyQuickBtns.forEach(b => b.setAttribute('aria-pressed', 'false'));
        btn.setAttribute('aria-pressed', 'true');
        if (state.submitAttempted || d.qtyInput.classList.contains('is-invalid')) validateQty();
      });
    });

    d.qtyInput.addEventListener('input', () => {
      d.qtyQuickBtns.forEach(b => b.setAttribute('aria-pressed', 'false'));
      if (state.submitAttempted || d.qtyInput.classList.contains('is-invalid')) validateQty();
    });

    d.qtyInput.addEventListener('blur', () => {
      // Einheitlich: nach blur immer validieren (wie collection/product die sofort reagieren)
      if (state.submitAttempted || d.qtyInput.value !== '') validateQty();
    });
  }

  function validateQty() {
    const raw     = d.qtyInput.value.trim();
    const val     = parseInt(raw, 10);
    const isValid = raw !== '' && !isNaN(val) && val >= 2;
    setError(d.qtyInput, d.qtyError, !isValid);
    return isValid;
  }

  /* ─────────────────────────────────────────────────────────
     ZEICHENZÄHLER
  ───────────────────────────────────────────────────────── */
  function initCharCount() {
    if (!d.bodyTextarea || !d.bodyCount) return;
    function update() {
      const len = d.bodyTextarea.value.length;
      d.bodyCount.textContent = `${len} / ${BODY_MAX}`;
      d.bodyCount.classList.toggle('is-near-limit', len >= BODY_MAX * 0.8 && len < BODY_MAX);
      d.bodyCount.classList.toggle('is-at-limit',   len >= BODY_MAX);
    }
    d.bodyTextarea.addEventListener('input', update);
    update();
  }

  /* ─────────────────────────────────────────────────────────
     KONTAKTFELDER – Live-Validierung (Fehler 5: einheitlich)
     Validierung läuft nach:
     - submitAttempted = true (nach erstem Klick)
     - Feld bereits als ungültig markiert (is-invalid)
  ───────────────────────────────────────────────────────── */
  function initContactValidation() {
    const fields = [
      { f: d.vorname,  e: d.vornameError,  ok: v => v.length > 0 },
      { f: d.nachname, e: d.nachnameError, ok: v => v.length > 0 },
      { f: d.email,    e: d.emailError,    ok: v => isValidEmail(v) },
    ];
    fields.forEach(({ f, e, ok }) => {
      if (!f) return;
      f.addEventListener('input', () => {
        if (state.submitAttempted || f.classList.contains('is-invalid')) {
          setError(f, e, !ok(f.value.trim()));
        }
      });
      f.addEventListener('blur', () => {
        // Einheitlich: nach blur validieren wenn Feld Wert hat oder submitAttempted
        if (state.submitAttempted || f.value.trim() !== '') setError(f, e, !ok(f.value.trim()));
      });
    });
  }

  /* ─────────────────────────────────────────────────────────
     E-MAIL-VALIDIERUNG (Fehler 6)
     Regex: RFC-5321 vereinfacht, praxistauglich.
     Testfälle:
       gültig:   user@example.com, a+b@sub.domain.ch, x@x.io
       ungültig: @example.com, user@, user, user@.com, user@com
  ───────────────────────────────────────────────────────── */
  function isValidEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
  }

  /* ─────────────────────────────────────────────────────────
     SUBMIT – System A: vollständig nativ (Shopify-Standard)
     =========================================================
     ARCHITEKTUR-ENTSCHEIDUNG:
     Shopify Contact Forms sind für nativen Submit konzipiert.
     fetch() ist inkompatibel mit Shopify hCaptcha und erzeugt
     Race-Conditions (zwei parallele Submit-Pfade).

     ABLAUF:
     1. Click-Handler (capture=true) auf Submit-Button
        → Eigene JS-Validierung läuft zuerst
        → Bei Fehlern: e.preventDefault() + stopImmediatePropagation()
          → Kein submit-Event → Kein Submit → Fehlermeldungen anzeigen
        → Bei validen Feldern: click läuft weiter
     2. Browser erzeugt submit-Event (type="submit")
        → Shopify hCaptcha-Script fängt ein (wenn aktiv) → Challenge
        → Nach Lösung: form.submit() nativ → Seite lädt neu
        → Ohne hCaptcha: nativer Submit → Seite lädt neu
     3. Seite lädt neu mit posted_successfully?=true
        → Liquid rendert Success-Komponente (Formular nicht gerendert)

     KEIN fetch(). KEIN submit-Event-Handler. KEIN JS-State für Erfolg.
  ───────────────────────────────────────────────────────── */
  function initFormSubmit() {
    if (!d.form || !d.submitBtn) return;

    // Sicherheit: Verhindert nativen Submit bei Browser-seitigem form.submit()
    // falls irgendetwas ausserhalb des click-Handlers einen Submit auslöst.
    // Wird nach erfolgreicher Validierung wieder aktiviert.
    // HINWEIS: Wir setzen KEIN generelles submit-preventDefault –
    // der native Submit muss nach valider Validierung durchlaufen.

    d.submitBtn.addEventListener('click', function (e) {
      // Honeypot (kein name-Attribut → nicht in FormData)
      const hp = document.getElementById('gravur-hp');
      if (hp && hp.value.length > 0) {
        e.preventDefault();
        return;
      }

      state.submitAttempted = true;
      syncIndiv();

      const errors = collectErrors();

      if (errors.length > 0) {
        // Validierungsfehler: Submit stoppen, Fehler anzeigen
        e.preventDefault();
        e.stopImmediatePropagation();
        scrollToFirstError(errors);
        return;
      }

      // Valide: click läuft weiter → Browser erzeugt submit-Event → nativer Submit
      // WICHTIG: setSubmitLoading() darf NICHT hier aufgerufen werden.
      // Ein disabled button blockiert den nativen submit-Event des Browsers.
      // Loading-State wird im submit-Handler gesetzt (nach click, vor Submit).

    }, true); // capture=true: läuft VOR allen anderen Listenern

    // Loading-State: wird im submit-Event gesetzt, NACHDEM der Browser
    // den Button als enabled geprüft hat und den Submit initiiert.
    // Kein preventDefault, kein fetch – ausschliesslich UX-Feedback.
    d.form.addEventListener('submit', function () {
      setSubmitLoading(true);
    });
  }

  function collectErrors() {
    const errors = [];

    const hasCol = !!(d.collectionSelect?.value);
    setError(d.collectionSelect, d.collectionError, !hasCol);
    if (!hasCol) errors.push({ el: d.collectionSelect, section: 'gravur-step-produkt' });

    if (hasCol && d.productStep && !d.productStep.hidden) {
      const hasProd = !!state.selectedProduct;
      setError(d.productSelect, d.productError, !hasProd);
      if (!hasProd) errors.push({ el: d.productSelect, section: 'gravur-step-produkt' });
    }

    if (!validateQty()) errors.push({ el: d.qtyInput, section: 'gravur-step-stueckzahl' });

    [
      { f: d.vorname,  e: d.vornameError,  ok: v => v.length > 0 },
      { f: d.nachname, e: d.nachnameError, ok: v => v.length > 0 },
      { f: d.email,    e: d.emailError,    ok: v => isValidEmail(v) },
    ].forEach(({ f, e, ok }) => {
      if (!f) return;
      const valid = ok(f.value.trim());
      setError(f, e, !valid);
      if (!valid) errors.push({ el: f, section: 'gravur-step-kontakt' });
    });

    return errors;
  }

  function scrollToFirstError(errors) {
    const first   = errors[0];
    const section = document.getElementById(first.section) || first.el?.closest('fieldset');
    if (section) section.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => { if (first.el) first.el.focus(); }, 360);
  }

  function setSubmitLoading(on) {
    if (!d.submitBtn) return;
    const label   = d.submitBtn.querySelector('.gravur-btn__label');
    const spinner = d.submitBtn.querySelector('.gravur-btn__spinner');
    if (label)   label.textContent = on ? 'Wird gesendet…' : 'Anfrage absenden';
    if (spinner) spinner.hidden    = !on;
    d.submitBtn.disabled = on;
    d.submitBtn.setAttribute('aria-busy', on ? 'true' : 'false');
  }



  /* ─────────────────────────────────────────────────────────
     FAQ ACCORDION
  ───────────────────────────────────────────────────────── */
  function initFAQ() {
    document.querySelectorAll('.gravur-faq__question').forEach(btn => {
      btn.addEventListener('click', function () {
        const expanded = this.getAttribute('aria-expanded') === 'true';
        const answer   = document.getElementById(this.getAttribute('aria-controls'));
        this.setAttribute('aria-expanded', !expanded);
        if (answer) answer.hidden = expanded;
      });
    });
  }

  /* ─────────────────────────────────────────────────────────
     HELPERS
  ───────────────────────────────────────────────────────── */

  /**
   * setError – Fehler 5: einheitliches Verhalten für SELECT und INPUT
   * is-valid wird gesetzt wenn:
   *   - kein Fehler UND
   *   - Feld hat einen Wert
   * Das gilt für alle Feldtypen (SELECT, INPUT, TEXTAREA).
   */
  function setError(inputEl, errorEl, hasError) {
    if (errorEl) errorEl.hidden = !hasError;
    if (inputEl) {
      inputEl.classList.toggle('is-invalid', hasError);
      const hasVal = inputEl.tagName === 'SELECT'
        ? inputEl.value !== ''
        : (inputEl.value?.trim().length > 0);
      inputEl.classList.toggle('is-valid', !hasError && hasVal);
      inputEl.setAttribute('aria-invalid', hasError ? 'true' : 'false');
    }
  }

  function populateSelect(sel, items, key, placeholder) {
    sel.innerHTML = `<option value="">${esc(placeholder)}</option>`;
    items.forEach(item => {
      const o = document.createElement('option');
      o.value = item.handle; o.textContent = item[key];
      sel.appendChild(o);
    });
  }

  function addErrorOption(sel, msg) {
    const o = document.createElement('option');
    o.disabled = true; o.textContent = msg;
    sel.appendChild(o);
  }

  function showSpinner(el, show) { if (el) el.hidden = !show; }

  function esc(s) {
    if (!s) return '';
    return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
                    .replace(/"/g,'&quot;').replace(/'/g,'&#39;');
  }

  /* ─────────────────────────────────────────────────────────
     INIT
  ───────────────────────────────────────────────────────── */
  async function init() {
    resolveDOM();
    if (!d.form) return;

    initHeroCTA();
    initFAQ();
    initQuantity();
    initCharCount();
    initIndividualisierung();
    initContactValidation();
    initCollectionSelect();
    initProductSelect();
    initProductClear();
    initFormSubmit();

    await loadCollections();
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init)
    : init();

})();
